import { useState, useEffect, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { mockUsers, type UserProfile } from "@/mocks/data";
import { getRoleLandingPath } from "@/mocks/rbac";
import { getRoleLabel } from "@/hooks/useRole";

import { authService } from "@/services/api";

const PARTICLES = [
  { top: "12%", left: "7%",  size: 3,   delay: 0,   dur: 4.2 },
  { top: "22%", left: "88%", size: 4,   delay: 0.7, dur: 5.1 },
  { top: "38%", left: "14%", size: 2,   delay: 1.4, dur: 3.8 },
  { top: "55%", left: "92%", size: 3.5, delay: 2.1, dur: 4.7 },
  { top: "70%", left: "6%",  size: 2.5, delay: 0.3, dur: 5.5 },
  { top: "82%", left: "78%", size: 3,   delay: 1.8, dur: 3.6 },
  { top: "15%", left: "55%", size: 2,   delay: 2.8, dur: 4.4 },
  { top: "48%", left: "48%", size: 1.5, delay: 1.1, dur: 6.2 },
  { top: "90%", left: "35%", size: 2.5, delay: 0.6, dur: 4.9 },
  { top: "6%",  left: "32%", size: 3,   delay: 3.3, dur: 5.8 },
  { top: "64%", left: "62%", size: 2,   delay: 1.5, dur: 3.9 },
  { top: "30%", left: "40%", size: 1.5, delay: 2.4, dur: 5.0 },
];

const FEATURE_PILLS = [
  { icon: "ri-shield-check-line",  text: "Casos con trazabilidad completa" },
  { icon: "ri-brain-line",         text: "IA normativa integrada"           },
  { icon: "ri-file-chart-line",    text: "Reportes descargables"            },
  { icon: "ri-team-line",          text: "Roles por área"                   },
];

const STATS = [
  { value: "500+", label: "Colegios en lista de espera", icon: "ri-building-4-line"   },
  { value: "10K+", label: "Consultas proyectadas",       icon: "ri-chat-3-line"        },
  { value: "99%",  label: "Precisión normativa IA",      icon: "ri-check-double-line"  },
  { value: "24/7", label: "Disponibilidad garantizada",  icon: "ri-time-line"          },
];

export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail]   = useState("");
  const [password, setPassword] = useState("");
  const [error, setError]   = useState("");
  const [loading, setLoading] = useState(false);
  const [showDemoAccounts, setShowDemoAccounts] = useState(false);
  const [focused, setFocused] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Trigger entrance animation after first paint
  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 60);
    return () => clearTimeout(t);
  }, []);

  const doLogin = (userData: any, access_token: string) => {
    localStorage.setItem("ezer_access_token", access_token);
    localStorage.setItem("ezer_user", JSON.stringify(userData));
    localStorage.setItem("convivencia_user", JSON.stringify({
      id: userData.id,
      nombre: userData.nombre,
      rol: userData.rol,
      email: userData.email,
      colegio: userData.tenant_id ? "Colegio Antigravity Demo" : "Plataforma SaaS"
    }));
    setExiting(true);
    setTimeout(() => navigate(getRoleLandingPath(userData.rol)), 650);
  };

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await authService.login({
        email: email.toLowerCase().trim(),
        password: password
      });
      const token = localStorage.getItem("ezer_access_token") || "";
      doLogin(user, token);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Credenciales incorrectas o error de conexión.");
      setLoading(false);
    }
  };

  const quickLogin = async (emailKey: string) => {
    const mapping: Record<string, { email: string, pass: string }> = {
      "director@liceovalentina.cl": { email: "director@colegio.cl", pass: "Ezer2026!" },
      "convivencia@liceovalentina.cl": { email: "convivencia@colegio.cl", pass: "Ezer2026!" },
      "orientador@liceovalentina.cl": { email: "orientador@colegio.cl", pass: "Ezer2026!" },
      "psicologo@liceovalentina.cl": { email: "psicologo@colegio.cl", pass: "Ezer2026!" },
      "inspector@liceovalentina.cl": { email: "inspector@colegio.cl", pass: "Ezer2026!" },
      "profesor@liceovalentina.cl": { email: "profesor@colegio.cl", pass: "Ezer2026!" },
      "docente@liceovalentina.cl": { email: "docente@colegio.cl", pass: "Ezer2026!" },
      "admin@liceovalentina.cl": { email: "admin@colegio.cl", pass: "Ezer2026!" },
      "sostenedor@liceovalentina.cl": { email: "sostenedor@colegio.cl", pass: "Ezer2026!" },
      "saas@convivencia.app": { email: "superadmin@ezer.cl", pass: "SuperAdmin2026!" },
      "directivo@liceovalentina.cl": { email: "director@colegio.cl", pass: "Ezer2026!" },
    };

    const target = mapping[emailKey];
    if (target) {
      setEmail(target.email);
      setPassword(target.pass);
      setError("");
      setLoading(true);
      try {
        const user = await authService.login({
          email: target.email,
          password: target.pass
        });
        const token = localStorage.getItem("ezer_access_token") || "";
        doLogin(user, token);
      } catch (err: any) {
        setError(err.response?.data?.detail || "Credenciales incorrectas o error de conexión.");
        setLoading(false);
      }
    }
  };

  const roleBadgeColor = (rol: string) => {
    const map: Record<string, string> = {
      director:              "bg-primary-100 text-primary-700 border-primary-200",
      convivencia:           "bg-red-50 text-red-600 border-red-100",
      encargado_convivencia: "bg-red-50 text-red-600 border-red-100",
      orientador:            "bg-amber-50 text-amber-700 border-amber-100",
      psicologo:             "bg-emerald-50 text-emerald-700 border-emerald-100",
      inspector:             "bg-sky-50 text-sky-700 border-sky-100",
      inspector_general:     "bg-sky-50 text-sky-700 border-sky-100",
      profesor_jefe:         "bg-violet-50 text-violet-700 border-violet-100",
      docente:               "bg-teal-50 text-teal-700 border-teal-100",
      admin:                 "bg-foreground-100 text-foreground-600 border-foreground-200",
      sostenedor:            "bg-foreground-100 text-foreground-600 border-foreground-200",
      superadmin:            "bg-indigo-50 text-indigo-700 border-indigo-200",
    };
    return map[rol] || "bg-foreground-100 text-foreground-600 border-foreground-200";
  };

  return (
    <div
      className={`min-h-screen flex relative overflow-hidden transition-all duration-700
        ${exiting ? "opacity-0 scale-[0.97] blur-sm" : "opacity-100 scale-100 blur-0"}
      `}
      style={{ background: "#f8fafc" }}
    >

      {/* ── Exit overlay on successful login ── */}
      {exiting && (
        <div className="absolute inset-0 z-50 flex items-center justify-center animate-fade-in animated-gradient">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-5 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center animate-float">
              <i className="ri-check-double-line text-white text-3xl animate-pulse" />
            </div>
            <p className="text-white text-xl font-black animate-fade-in-up" style={{ animationDelay: "100ms" }}>
              ¡Bienvenido/a!
            </p>
            <p className="text-white/80 text-sm mt-1 animate-fade-in-up font-medium" style={{ animationDelay: "200ms" }}>
              Cargando tu espacio de trabajo...
            </p>
            <div className="flex gap-1 justify-center mt-4">
              {[0,1,2].map(i => (
                <span key={i} className="w-2.5 h-2.5 rounded-full bg-white/70 animate-bounce" style={{ animationDelay: `${i * 120}ms` }} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Animated gradient BG ── */}
      <div className="absolute inset-0 animated-gradient" />

      {/* ── Pulsing and Floating Orbs ── */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute animate-float bg-white/10 blur-3xl rounded-full"
          style={{ top: "-10%", right: "-10%", width: 500, height: 500, animationDelay: "0ms" }} />
        <div className="absolute animate-float bg-white/10 blur-3xl rounded-full"
          style={{ bottom: "-15%", left: "-5%", width: 600, height: 600, animationDelay: "1s" }} />
        <div className="absolute animate-float bg-white/10 blur-3xl rounded-full"
          style={{ top: "35%", left: "30%", width: 450, height: 450, animationDelay: "2s" }} />
        {/* 2 small pulsing dots */}
        <div className="absolute w-3 h-3 bg-white rounded-full animate-pulse-soft opacity-60" style={{ top: "25%", left: "75%" }} />
        <div className="absolute w-2 h-2 bg-white rounded-full animate-pulse-soft opacity-40" style={{ bottom: "35%", right: "80%" }} />
      </div>

      {/* ── Floating particles ── */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {PARTICLES.map((p, i) => (
          <span
            key={i}
            className="absolute rounded-full"
            style={{
              top: p.top, left: p.left,
              width: p.size * 3, height: p.size * 3,
              background: "rgba(255, 255, 255, 0.15)",
              animation: `float ${p.dur}s ease-in-out infinite`,
              animationDelay: `${p.delay}s`,
            }}
          />
        ))}
      </div>

      {/* Floating badge in top right corner */}
      <div className="absolute top-8 right-8 bg-white/15 backdrop-blur-sm px-4 py-1.5 rounded-full text-xs font-bold text-white border border-white/20 shadow-lg animate-floating-badge z-20">
        🚀 Ezer OS v2.0
      </div>

      {/* ── Left panel — branding ── */}
      <div className="hidden lg:flex lg:w-[48%] relative items-center justify-center px-12 z-10">
        <div
          className={`relative z-10 max-w-lg w-full transition-all duration-700 ease-out
            ${mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          {/* Logo */}
          <div
            className="flex items-center gap-4 mb-10"
            style={{ opacity: mounted ? 1 : 0, transform: mounted ? "translateX(0)" : "translateX(-20px)", transition: "all 0.6s cubic-bezier(0.16,1,0.3,1)" }}
          >
            <div className="relative">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center shadow-lg animate-logo-pulse">
                <i className="ri-shield-star-fill text-white text-2xl" />
              </div>
              <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-pink-500 flex items-center justify-center shadow-md animate-breathe">
                <i className="ri-sparkling-line text-white text-[10px]" />
              </div>
            </div>
            <div>
              <h1 className="text-3xl font-black tracking-tight text-white">
                Ezer <span className="text-pink-300">Convivencia360</span>
              </h1>
              <p className="text-sm text-white/70 mt-0.5 font-medium">Plataforma de Gestión Escolar</p>
            </div>
          </div>

          {/* Pill de contexto */}
          <div 
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 text-xs text-white font-bold mb-6"
            style={{ opacity: mounted ? 1 : 0, transform: mounted ? "translateY(0)" : "translateY(10px)", transition: "all 0.5s cubic-bezier(0.16,1,0.3,1) 0.05s" }}
          >
            <i className="ri-sparkling-fill text-pink-300 animate-pulse-soft" />
            <span>El futuro de la gestión escolar</span>
          </div>

          {/* Hero text */}
          <div
            className="mb-10"
            style={{ opacity: mounted ? 1 : 0, transform: mounted ? "translateY(0)" : "translateY(16px)", transition: "all 0.65s cubic-bezier(0.16,1,0.3,1) 0.1s" }}
          >
            <h2 className="text-5xl font-black leading-tight text-white tracking-tight">
              Gestión escolar <br />
              <span className="relative inline-block mt-2">
                inteligente
                <svg className="absolute -bottom-2 left-0 w-full h-2 text-pink-300/80" viewBox="0 0 100 10" preserveAspectRatio="none">
                  <path d="M0,7 C30,2 70,2 100,7" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round" />
                </svg>
              </span>
            </h2>
            <p className="text-base text-white/70 leading-relaxed mt-6 max-w-md font-medium">
              Centraliza la convivencia escolar, orientación y psicología de tu colegio
              en una plataforma moderna, segura y diseñada para equipos educativos.
            </p>
          </div>

          {/* Feature pills */}
          <div
            className="flex flex-wrap gap-2.5 mb-10"
            style={{ opacity: mounted ? 1 : 0, transition: "opacity 0.6s 0.2s" }}
          >
            {FEATURE_PILLS.map((f, i) => (
              <span
                key={f.text}
                className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-white/15 backdrop-blur-sm border border-white/10 text-sm text-white font-medium hover:bg-white/25 transition-all duration-200 cursor-default"
                style={{ opacity: mounted ? 1 : 0, transform: mounted ? "translateY(0)" : "translateY(10px)", transition: `all 0.5s cubic-bezier(0.16,1,0.3,1) ${0.25 + i * 0.07}s` }}
              >
                <i className={`${f.icon} text-pink-300 text-base`} />
                {f.text}
              </span>
            ))}
          </div>

          {/* Stats cards */}
          <div
            className="grid grid-cols-2 gap-3"
            style={{ opacity: mounted ? 1 : 0, transform: mounted ? "translateY(0)" : "translateY(20px)", transition: "all 0.65s cubic-bezier(0.16,1,0.3,1) 0.35s" }}
          >
            {STATS.map((stat, i) => (
              <div
                key={stat.label}
                className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100/50 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-default"
                style={{ animationDelay: `${i * 80}ms` }}
              >
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500/10 to-pink-500/10 flex items-center justify-center mb-3">
                  <i className={`${stat.icon} text-primary-600 text-lg`} />
                </div>
                <p className="text-3xl font-black text-gray-900">{stat.value}</p>
                <p className="text-xs font-semibold text-gray-500 mt-1 leading-tight">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Right panel — login form ── */}
      <div className="flex-1 flex items-center justify-center px-4 py-8 relative z-10">
        <div className="w-full max-w-[420px]">

          {/* Mobile logo */}
          <div
            className="lg:hidden text-center mb-8"
            style={{ opacity: mounted ? 1 : 0, transform: mounted ? "translateY(0)" : "translateY(-12px)", transition: "all 0.5s cubic-bezier(0.16,1,0.3,1)" }}
          >
            <div className="relative inline-block">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center mx-auto shadow-lg animate-logo-pulse">
                <i className="ri-shield-star-fill text-white text-2xl" />
              </div>
              <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-pink-500 flex items-center justify-center shadow-md animate-breathe">
                <i className="ri-sparkling-line text-white text-[10px]" />
              </div>
            </div>
            <h1 className="text-2xl font-black text-white mt-3">
              Ezer <span className="text-pink-300">Convivencia360</span>
            </h1>
            <p className="text-sm text-white/70 mt-1 font-medium">Plataforma de Gestión Escolar</p>
          </div>

          {/* Form card */}
          <div
            className={`bg-white/95 backdrop-blur-2xl rounded-3xl p-8 transition-all duration-500 border border-white/50 shadow-2xl
              ${mounted ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-6 scale-[0.98]"}
            `}
            style={{
              transition: "opacity 0.65s cubic-bezier(0.16,1,0.3,1) 0.15s, transform 0.65s cubic-bezier(0.16,1,0.3,1) 0.15s, border-color 0.3s",
            }}
          >
            {/* Sparkles Header Banner */}
            <div className="w-full bg-gradient-to-r from-violet-50 to-pink-50 rounded-2xl p-4 mb-6 flex items-center gap-3 border border-violet-100/50">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center text-white shadow-md animate-pulse-soft">
                <i className="ri-sparkling-fill text-lg" />
              </div>
              <div>
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="text-[10px] font-bold text-primary-950 uppercase tracking-wider">Acceso Seguro</span>
                </div>
                <p className="text-xs font-bold text-primary-700 mt-0.5">Potenciado por IA normativa</p>
              </div>
            </div>

            {/* Header */}
            <div className="mb-6">
              <h2 className="text-2xl font-black text-gray-900 leading-tight">Bienvenido de nuevo</h2>
              <p className="text-sm text-gray-500 mt-1.5 leading-relaxed">
                Ingresa con tus credenciales institucionales para continuar
              </p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Email institucional
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <i className="ri-mail-line text-gray-400 group-focus-within:text-primary-500 transition-colors duration-200" />
                  </div>
                  <input
                    type="email"
                    placeholder="tu.nombre@colegio.cl"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 pl-10 pr-4 py-3 outline-none
                      placeholder:text-gray-400 transition-all duration-200
                      focus:bg-white focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Contraseña
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <i className="ri-lock-line text-gray-400 group-focus-within:text-primary-500 transition-colors duration-200" />
                  </div>
                  <input
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 pl-10 pr-4 py-3 outline-none
                      placeholder:text-gray-400 transition-all duration-200
                      focus:bg-white focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500"
                    required
                  />
                </div>
              </div>

              {error && (
                <div className="flex items-start gap-2.5 p-3.5 rounded-xl bg-red-50/90 backdrop-blur-sm border border-red-100 animate-slide-down">
                  <i className="ri-error-warning-line text-red-500 mt-0.5 flex-shrink-0 animate-icon-wiggle" />
                  <p className="text-sm text-red-700 font-medium">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full text-white text-sm font-bold py-3.5 rounded-xl transition-all duration-300
                  active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed
                  cursor-pointer whitespace-nowrap flex items-center justify-center gap-2
                  hover:scale-[1.01] hover:shadow-[0_10px_25px_rgba(124,58,237,0.5)] active:scale-[0.98]
                  animated-gradient shadow-[0_4px_14px_rgba(124,58,237,0.3)] duration-300"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Verificando credenciales...</span>
                  </>
                ) : (
                  <>
                    <i className="ri-login-box-line text-base" />
                    <span>Ingresar a Ezer Convivencia360</span>
                    <i className="ri-arrow-right-line text-base" />
                  </>
                )}
              </button>
            </form>

            {/* Divider */}
            <div className="relative my-5">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-150" />
              </div>
              <div className="relative flex justify-center">
                <span className="px-3 bg-white text-[10px] text-gray-400 font-bold uppercase tracking-wider rounded-full border border-gray-100">
                  Acceso rápido · Demo
                </span>
              </div>
            </div>

            {/* Demo accounts */}
            <div>
              <button
                onClick={() => setShowDemoAccounts(!showDemoAccounts)}
                className="flex items-center justify-center gap-2 w-full text-sm text-gray-500 hover:text-primary-600 transition-colors cursor-pointer py-1 font-semibold"
              >
                <i className={`${showDemoAccounts ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} transition-transform duration-300`} />
                {showDemoAccounts ? "Ocultar cuentas demo" : "Ver cuentas de demostración"}
              </button>

              <div className={`transition-all duration-400 overflow-hidden ${showDemoAccounts ? "max-h-[560px] opacity-100 mt-3" : "max-h-0 opacity-0"}`}>
                <div className="space-y-1.5 max-h-[500px] overflow-y-auto pr-1">
                  {Object.entries(mockUsers)
                    .filter(([key]) => key !== "directivo@liceovalentina.cl")
                    .map(([userEmail, user], idx) => (
                      <button
                        key={userEmail}
                        onClick={() => quickLogin(userEmail)}
                        className="flex items-center justify-between w-full p-3 rounded-xl border border-gray-100 bg-white/70 backdrop-blur-sm
                          hover:bg-violet-50/50 hover:border-violet-200/60 hover:translate-x-1 hover:shadow-sm
                          transition-all duration-300 ease-out cursor-pointer text-left group animate-sidebar-item"
                        style={{ animationDelay: `${idx * 35}ms` }}
                      >
                        <div className="flex items-center gap-2.5 min-w-0 flex-1">
                          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-primary-400 to-pink-500 flex items-center justify-center flex-shrink-0">
                            <span className="text-[11px] font-bold text-white">{user.nombre.charAt(0)}</span>
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-800 truncate group-hover:text-primary-800 transition-colors">{user.nombre}</p>
                            <p className="text-xs text-gray-400 truncate mt-0.5">{user.colegio}</p>
                          </div>
                        </div>
                        <span className={`text-[10px] font-bold tracking-wider uppercase px-2.5 py-1 rounded-full whitespace-nowrap flex-shrink-0 ml-2 border ${roleBadgeColor(user.rol)}`}>
                          {getRoleLabel(user.rol)}
                        </span>
                      </button>
                    ))}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <p
            className="text-center text-xs text-white/60 mt-5 font-semibold"
            style={{ opacity: mounted ? 1 : 0, transition: "opacity 0.6s 0.5s" }}
          >
            Ezer Convivencia360 &copy; 2026 &middot; Plataforma de Gestión de Convivencia Escolar
          </p>
        </div>
      </div>
    </div>
  );

}